import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {HttpErrors} from '@common/core/http/errors/http-errors.enum';
import {Link} from '../../../shared/link/link';
import {
    CrupdateLinkModalComponent,
    CrupdateLinkModalData,
} from '../crupdate-link-modal/crupdate-link-modal.component';
import {LinkService} from '../../../shared/link/link.service';
import {BackendErrorResponse} from '@common/core/types/backend-error-response';
import {DatatableService} from '@common/datatable/datatable.service';
import {CurrentUser} from '@common/auth/current-user';
import {Toast} from '@common/core/ui/toast.service';
import {ActivatedRoute, Router} from '@angular/router';
import {Messages} from '../../../messages.enum';
import {LinkUsageService} from '../../../shared/link/link-usage.service';
import {LINK_INDEX_FILTERS} from './link-index-filters';
import {CsvExporterService} from '@common/csv/csv-exporter.service';
import {WorkspacesService} from '@common/workspaces/workspaces.service';
import {LinkGroupService} from '../../../shared/link/link-group.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {LinkGroup} from '../../../shared/link/link-group';
import {ConfirmModalComponent} from '@common/core/ui/confirm-modal/confirm-modal.component';
import {Modal} from '@common/core/ui/dialogs/modal.service';
import {
    AttachLinkModalComponent,
    AttachLinkModalData,
} from '../../link-group/attach-link-modal/attach-link-modal.component';
import {ActiveAppSectionService} from '../../active-app-section.service';
import {DatatableFilter} from '@common/datatable/datatable-filters/search-input-with-filters/filter-config/datatable-filter';

@Component({
    selector: 'link-index',
    templateUrl: './link-index.component.html',
    styleUrls: ['./link-index.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DatatableService],
})
export class LinkIndexComponent implements OnInit, OnDestroy {
    @Input() groupId: number;
    filters: DatatableFilter[];
    group$ = new BehaviorSubject<LinkGroup>(null);
    links$ = this.datatable.data$ as Observable<Link[]>;
    private workspaceSub: Subscription;

    constructor(
        public datatable: DatatableService<Link>,
        public currentUser: CurrentUser,
        private links: LinkService,
        private toast: Toast,
        private route: ActivatedRoute,
        private linkUsage: LinkUsageService,
        private csv: CsvExporterService,
        private router: Router,
        private modal: Modal,
        private groups: LinkGroupService,
        private workspaces: WorkspacesService,
        private activeSection: ActiveAppSectionService
    ) {}

    ngOnInit() {
        this.workspaceSub = this.workspaces.activeId$.subscribe(() => {
            this.setFilters();
        });
        this.datatable.init({
            uri: this.groupId
                ? `${LinkGroupService.BASE_URI}/${this.groupId}/links`
                : LinkService.BASE_URI,
            staticParams: {
                with: ['user'],
                userId: !this.showUserColumn()
                    ? this.currentUser.get('id')
                    : null,
            },
        });
        if (this.groupId) {
            this.datatable.paginator.response$.subscribe(response => {
                this.group$.next(response?.linkGroup);
            });
        }
    }

    ngOnDestroy() {
        this.workspaceSub.unsubscribe();
    }

    maybeDeleteSelectedLinks(linkIds?: number[]) {
        this.datatable.confirmResourceDeletion('links').subscribe(() => {
            this.links
                .delete(linkIds || this.datatable.selectedRows$.value)
                .subscribe(
                    () => {
                        this.datatable.reset();
                        this.linkUsage.reload();
                        this.toast.open(Messages.LINK_DELETE_SUCCESS);
                    },
                    (errResponse: BackendErrorResponse) => {
                        this.toast.open(
                            errResponse.message || HttpErrors.Default
                        );
                    }
                );
        });
    }

    showAttachLinksToGroupModal() {
        this.modal
            .open(AttachLinkModalComponent, {
                groupId: this.groupId,
            } as AttachLinkModalData)
            .beforeClosed()
            .subscribe(data => {
                if (data) {
                    this.datatable.reset();
                }
            });
    }

    maybeDetachLinksFromGroup() {
        this.modal
            .open(ConfirmModalComponent, {
                title: `Detach Links`,
                body: `Are you sure you want to detach selected links from this group?`,
                ok: 'Detach',
            })
            .afterClosed()
            .subscribe(confirmed => {
                if (!confirmed) return;
                this.groups
                    .detach(this.groupId, this.datatable.selectedRows$.value)
                    .subscribe(
                        () => this.datatable.reset(),
                        (err: BackendErrorResponse) => {
                            this.toast.open(err.message || HttpErrors.Default);
                        }
                    );
            });
    }

    showCrupdateLinkModal(link?: Link, batch?: boolean) {
        this.datatable
            .openCrupdateResourceModal(CrupdateLinkModalComponent, {
                link,
                groups: {[this.groupId]: {}},
                batch,
            } as CrupdateLinkModalData)
            .subscribe(() => this.linkUsage.reload());
    }

    showUserColumn(): boolean {
        // show in admin and if active workspace is not personal
        return this.activeSection.admin || !!this.workspaces.activeId$.value;
    }

    private setFilters() {
        this.filters = LINK_INDEX_FILTERS.filter(
            f => f.key !== 'user_id' || this.showUserColumn()
        );
    }

    exportCsv() {
        this.csv.export(LinkService.EXPORT_CSV_URI, {
            type: this.activeSection.admin ? 'all' : null,
        });
    }

    getParentRoute(): string {
        return this.activeSection.admin ? 'admin' : 'dashboard';
    }
}
